import axios from 'axios'
import { toast } from 'react-toastify'

const FACEBOOK_API = 'https://graph.facebook.com/v16.0'

export const getUserInfo = async (accessToken: string) => {
  const url = `https://graph.facebook.com/v14.0/me?fields=id,name,picture&access_token=${accessToken}`
  const response = await axios.get(url)
  const user = response.data
  return { id: user.id, name: user.name, picture: user.picture?.data?.url }
}

export const getUserPages = async (userId: string, accessToken: string) => {
  const url = `https://graph.facebook.com/v14.0/${userId}/accounts?fields=id,name,picture,access_token&access_token=${accessToken}`
  const response = await axios.get(url)
  const pages = response.data.data.map((page: any) => {
    return {
      id: page.id,
      name: page.name,
      picture: page.picture?.data?.url,
      accessToken: page.access_token
    }
  })
  return pages
}

const loadFacebookSDKScript = (facebookElementId: string, facebookSDKSrc: string) => {
  const d = document
  const s = 'script'
  // Check if facebook sdk script is loaded
  if (document.getElementById(facebookElementId)) {
    return
  }
  const scriptElement = document.createElement(s)
  scriptElement.id = facebookElementId
  scriptElement.async = true
  scriptElement.src = facebookSDKSrc
  const firstScriptElement = d.getElementsByTagName(s)[0]
  firstScriptElement.parentNode?.insertBefore(scriptElement, firstScriptElement)
}

const setFacebookInitFunction = (facebookAPIVersion: string, facebookAppId: string | undefined) => {
  window.fbAsyncInit = function() {
    window.FB.init({
      appId: facebookAppId,
      autoLogAppEvents: true,
      xfbml: true,
      version: facebookAPIVersion
    })
    window.FB.AppEvents.logPageView()
  }
}

export const initFacebookSDK = (
  facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID,
  facebookAPIVersion = 'v16.0',
  facebookElementId = 'facebook-jssdk',
  facebookSDKSrc = 'https://connect.facebook.net/en_US/sdk.js'
) => {
  // wait for facebook sdk to initialize before starting the react app
  setFacebookInitFunction(facebookAPIVersion, facebookAppId)

  // load facebook sdk script
  loadFacebookSDKScript(facebookElementId, facebookSDKSrc)
}

export const addFacebookAccount = (scope: string) =>
  new Promise<fb.StatusResponse>(resolve =>
    window.FB.login(resolve, {
      scope,
      // eslint-disable-next-line @typescript-eslint/camelcase
      return_scopes: true,
      // eslint-disable-next-line @typescript-eslint/camelcase
      enable_profile_selector: true
    })
  )

/**
 * Ensures that all requested and required scopes have been granted by Facebook
 */
export const validateFbGrantedScope = (requestedScope: string[], grantedScope: string[]) => {
  for (const scope of requestedScope) {
    if (!grantedScope.includes(scope)) {
      return false
    }
  }
  return true
}

export const getFacebookAccount = async (accessToken: string, id: string, name?: string) => {
  try {
    const { data } = await axios.get(`${FACEBOOK_API}/me`, {
      params: {
        // eslint-disable-next-line @typescript-eslint/camelcase
        access_token: accessToken,
        fields: 'id,name,picture'
      }
    })

    return { ...data, picture: data?.picture?.data?.url }
  } catch (error) {
    toast.error(`Unable to access user data: ${name ?? id}`)
    return { id, name }
  }
}

export const clearFacebookLoginData = () =>
  new Promise<fb.StatusResponse>(resolve => window.FB.logout(resolve))

export const revokeFacebookAccountToken = async (accessToken: string) => {
  const { data } = await axios.delete(
    `https://graph.facebook.com/v16.0/me/permissions?access_token=${accessToken}`
  )
  return data
}

export const getFacebookPages = async (accessToken: string) => {
  try {
    const { data } = await axios.get(`${FACEBOOK_API}/me/accounts`, {
      params: {
        // eslint-disable-next-line @typescript-eslint/camelcase
        access_token: accessToken,
        fields: 'id,name,picture,access_token.as(accessToken)'
      }
    })
    return (
      data?.data.map(value => ({
        ...value,
        picture: value?.picture?.data?.url
      })) ?? []
    )
  } catch (error) {
    toast.error(`Unable to access user pages`)
    return []
  }
}
